/**
 * api/sessions.ts — `/api/sessions` lifecycle.
 *
 * Carved from `src/stores/useApiStore.ts`. The store keeps the
 * journey-touch + session-expiry orchestration; the wire calls move
 * here. The headers contract — Content-Type + X-Journey-Id +
 * optional X-Session-Token — is the store's concern (it owns the
 * expiry / journey state), so these calls take a `headers` parameter
 * the store fills in.
 *
 * Note: `useApiStore` retains a `request<T>(url, options)` internal
 * helper because its endpoints route through a single error-classifier
 * (the `ProxyUnreachableError` shape A4 documented). The session
 * functions below are typed-call thin wrappers consumers can use
 * outside the store if needed (none today, but the boundary stays
 * uniform).
 */

import { apiFetch } from "./client.ts";

export interface CreateSessionResponse {
    sessionId: string;
    sessionActiveUntil: string;
    /** Discriminator: `true` when the server returns a synthetic session
     *  because persistence is unavailable (degraded-dev). Optional, engine-
     *  ignored — the client treats the session the same in either case; the
     *  flag exists for log correlation + future telemetry. */
    ephemeral?: boolean;
}

export interface SubmitResultBody {
    status: "partial" | "complete";
    download?: number | null;
    upload?: number | null;
    ping?: number | null;
    jitter?: number | null;
    raw?: Record<string, unknown>;
    variant?: string;
    journeyId?: string;
}

export async function createSession(
    headers: Record<string, string>,
): Promise<CreateSessionResponse> {
    return apiFetch<CreateSessionResponse>("/api/sessions", {
        method: "POST",
        headers,
    });
}

export async function submitSessionResult(
    body: SubmitResultBody,
    headers: Record<string, string>,
): Promise<void> {
    await apiFetch<unknown>("/api/results", {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });
}
